import type { PartyAck, PartyClientToServerEvents } from '@sorrem/shared';

/** How long a party request waits for the server's answer before giving up, so a lost one can be tried again. */
export const PARTY_ACK_TIMEOUT_MS = 10_000;

/** What a request the server never answered resolves with. */
export const NO_ANSWER_ERROR = 'The party did not answer. Try again.';

/** The party requests whose only argument is the server's answer. */
export type PartyAckOnlyEvent = {
    [Event in keyof PartyClientToServerEvents]: Parameters<PartyClientToServerEvents[Event]> extends [(response: PartyAck) => void]
        ? Event
        : never;
}[keyof PartyClientToServerEvents];

/**
 * Sends a request with `send` and resolves with the server's answer, or
 * null when none comes within `PARTY_ACK_TIMEOUT_MS`: a socket torn down
 * mid-request never calls back, which used to leave the caller waiting
 * for good (a Ready toggle stuck disabled).
 */
export function awaitAck(send: (ack: (response: PartyAck) => void) => void): Promise<PartyAck | null> {
    return new Promise((resolve) => {
        const timer = setTimeout(() => resolve(null), PARTY_ACK_TIMEOUT_MS);
        send((response) => {
            clearTimeout(timer);
            resolve(response);
        });
    });
}

/** A request's refusal as the reason to show: null when the server accepted it. */
export function resolveAckError(response: PartyAck | null): string | null {
    if (response === null) {
        return NO_ANSWER_ERROR;
    }
    return response.ok ? null : response.error;
}
